import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {ResizeObserver | undefined} */
  #folderTabsObserver;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#observeFolderTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#folderTabsObserver?.disconnect();
  }

  /**
   * Folder drawer (drawer_folders): stagger the tabs like real folders, so no tab
   * sits in front of the tab of the folder just behind it. Each tab can start at
   * the left edge, where the tab behind it ends, or flush right; every
   * combination is scored (overlap with the tab behind costs most, then stepping
   * back left) and the cheapest wins. Five or so links make this instant.
   */
  #placeFolderTabs = () => {
    const list = this.querySelector('.menu-drawer--folders .menu-drawer__menu.has-submenu');
    if (!(list instanceof HTMLElement) || !list.clientWidth) return;

    /** @type {{ item: HTMLElement, width: number }[]} */
    const tabs = [];
    for (const item of list.children) {
      const tab = item.querySelector('.menu-drawer__menu-item--mainlist');
      if (item instanceof HTMLElement && tab instanceof HTMLElement) tabs.push({ item, width: tab.offsetWidth });
    }
    if (!tabs.length) return;

    const room = list.clientWidth;
    /** @type {number[]} */
    let best = [];
    let bestCost = Infinity;

    /**
     * @param {number} index
     * @param {number[]} starts
     * @param {number} cost
     */
    const search = (index, starts, cost) => {
      if (cost >= bestCost) return;
      if (index === tabs.length) {
        best = starts;
        bestCost = cost;
        return;
      }

      const width = tabs[index]?.width ?? 0;
      const last = Math.max(room - width, 0);
      if (index === 0) return search(1, [0], 0);

      const previousStart = starts[index - 1] ?? 0;
      const previousEnd = previousStart + (tabs[index - 1]?.width ?? 0);
      const options = new Set([0, Math.min(previousEnd, last), last]);

      for (const start of options) {
        const overlap = Math.max(0, Math.min(previousEnd, start + width) - Math.max(previousStart, start));
        const stepBack = start <= previousStart ? 10 : 0;
        search(index + 1, [...starts, start], cost + overlap * 1000 + stepBack + Math.abs(start - previousEnd) * 0.01);
      }
    };
    search(0, [], 0);

    tabs.forEach(({ item }, index) => item.style.setProperty('--folder-x', `${best[index] ?? 0}px`));
    list.dataset.tabsPlaced = 'true';
  };

  /**
   * Re-place the folder tabs whenever the drawer's width changes (it has no
   * width while closed, so this also runs as it opens).
   */
  #observeFolderTabs() {
    const list = this.querySelector('.menu-drawer--folders .menu-drawer__menu.has-submenu');
    if (!list) return;

    this.#folderTabsObserver?.disconnect();
    this.#folderTabsObserver = new ResizeObserver(this.#placeFolderTabs);
    this.#folderTabsObserver.observe(list);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
